import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from './api';
import { softphone } from './softphone';
import { useNova } from './store';
import type { AgentState, InteractionRow, LiveCall } from './types';

/** Runs an API call, shows a toast on failure (and on success if given text). */
export async function act<T>(fn: () => Promise<T>, okText?: string): Promise<T | undefined> {
  try {
    const r = await fn();
    if (okText) useNova.getState().toast(okText, 'success');
    return r;
  } catch (err) {
    useNova.getState().toast(err instanceof ApiError ? err.message : 'Something went wrong', 'error');
    return undefined;
  }
}

export function setMyState(state: AgentState, reason?: string) {
  return act(() => api('PUT', '/agents/me/state', { state, reason: reason ?? null }));
}

export function toggleHold(call: LiveCall) {
  return act(() => api('POST', `/calls/${call.interactionId}/${call.onHold ? 'unhold' : 'hold'}`));
}

export function toggleMute(call: LiveCall) {
  return act(() => api('POST', `/calls/${call.interactionId}/mute`, { muted: !call.muted }));
}

/** Pause the recording while the caller gives card details, then resume. */
export function toggleRecordingPause(call: LiveCall) {
  return act(
    () => api('POST', `/calls/${call.interactionId}/recording`, { paused: !call.recordingPaused }),
    call.recordingPaused ? 'Recording resumed' : 'Recording paused',
  );
}

export async function hangup(call: LiveCall) {
  const r = await act(() => api('POST', `/calls/${call.interactionId}/hangup`));
  // API unreachable: at least drop the audio on this device
  if (r === undefined && useNova.getState().calls[call.interactionId]) softphone.hangupLocal();
}

/** True once the call is placed (a test call to our own number returns no call record). */
export async function dial(number: string): Promise<boolean> {
  return Boolean(await act(() => api('POST', '/calls/originate', { number }).then(() => true)));
}

export function formatNumber(n: string): string {
  const d = n.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('254')) return `+254 ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
  if (d.length > 8) return `+${d}`;
  return n;
}

/** Past interactions with one phone number, newest first. */
export function useHistory(number: string | null) {
  const [rows, setRows] = useState<InteractionRow[] | null>(null);
  const key = useMemo(() => number?.replace(/\D/g, '') ?? '', [number]);
  useEffect(() => {
    if (!key) {
      setRows(null);
      return;
    }
    let live = true;
    api<InteractionRow[]>('GET', `/interactions?number=${encodeURIComponent(key)}&limit=10`)
      .then((r) => live && setRows(r))
      .catch(() => live && setRows([]));
    return () => {
      live = false;
    };
  }, [key]);
  return rows;
}
